import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Crosshair, Map as MapIcon, List, X } from "lucide-react";
import { getDiscounts } from "@/lib/api";
import { CATEGORY_LIST } from "@/lib/categories";
import DiscountCard from "@/components/DiscountCard";
import CategoryChip from "@/components/CategoryChip";
import MapView from "@/components/MapView";

export default function Browse() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [view, setView] = useState("list"); // mobile: list | map
    const [mapCenter, setMapCenter] = useState(null);

    const category = params.get("category") || "all";
    const q = params.get("q") || "";
    const city = params.get("city") || "";
    const state = params.get("state") || "";
    const lat = params.get("lat");
    const lng = params.get("lng");
    const radius = params.get("radius");

    const fetchData = () => {
        setLoading(true);
        const payload = {};
        if (category && category !== "all") payload.category = category;
        if (q) payload.q = q;
        if (city) payload.city = city;
        if (state) payload.state = state;
        if (lat && lng) {
            payload.lat = lat;
            payload.lng = lng;
            payload.radius = radius || 50;
        }
        getDiscounts(payload)
            .then((res) => setData(res))
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [params.toString()]);

    useEffect(() => {
        if (lat && lng) setMapCenter([parseFloat(lat), parseFloat(lng)]);
    }, [lat, lng]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value || value === "all") next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    const useMyLocation = () => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition((pos) => {
            const next = new URLSearchParams(params);
            next.set("lat", pos.coords.latitude);
            next.set("lng", pos.coords.longitude);
            next.set("radius", "50");
            setParams(next);
        });
    };

    const clearGeo = () => {
        const next = new URLSearchParams(params);
        ["lat", "lng", "radius"].forEach((k) => next.delete(k));
        setParams(next);
    };

    const counts = useMemo(() => {
        const c = {};
        data.forEach((d) => { c[d.category] = (c[d.category] || 0) + 1; });
        return c;
    }, [data]);

    const handleCardHover = (id) => setActiveId(id);

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
            {/* Header + Filters */}
            <div className="mb-6">
                <div className="mono-label text-volt mb-1">// DIRECTORY</div>
                <h1 className="font-oswald uppercase tracking-wide text-3xl md:text-4xl mb-4">
                    Recon Board
                </h1>

                <div className="flex flex-col md:flex-row gap-2 mb-4">
                    <div className="flex items-center gap-2 bg-[#0e1319] border border-white/15 focus-within:border-volt px-3 py-2.5 flex-1">
                        <Search className="w-4 h-4 text-volt" />
                        <input
                            data-testid="browse-search-keyword"
                            defaultValue={q}
                            onKeyDown={(e) => e.key === "Enter" && updateParam("q", e.target.value)}
                            onBlur={(e) => updateParam("q", e.target.value)}
                            placeholder="Search brand, keyword..."
                            className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-[#64748b]"
                        />
                    </div>
                    <div className="flex items-center gap-2 bg-[#0e1319] border border-white/15 focus-within:border-volt px-3 py-2.5 md:w-56">
                        <MapPin className="w-4 h-4 text-volt" />
                        <input
                            data-testid="browse-search-city"
                            defaultValue={city}
                            onBlur={(e) => updateParam("city", e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && updateParam("city", e.target.value)}
                            placeholder="City"
                            className="bg-transparent flex-1 outline-none text-sm text-white placeholder:text-[#64748b]"
                        />
                    </div>
                    <button
                        data-testid="browse-use-location"
                        onClick={useMyLocation}
                        className="btn-outline-volt justify-center text-xs"
                    >
                        <Crosshair className="w-4 h-4" /> Near Me
                    </button>
                </div>

                {(lat && lng) && (
                    <div className="flex items-center gap-2 mb-4">
                        <span className="mono-label text-volt">RADIUS LOCK · {radius || 50} MI</span>
                        <button
                            data-testid="clear-geo-btn"
                            onClick={clearGeo}
                            className="mono-label text-[#94a3b8] hover:text-white inline-flex items-center gap-1"
                        >
                            <X className="w-3 h-3" /> clear
                        </button>
                    </div>
                )}

                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                    <CategoryChip
                        slug="all"
                        active={category === "all"}
                        onClick={() => updateParam("category", "all")}
                    />
                    {CATEGORY_LIST.map((c) => (
                        <CategoryChip
                            key={c.slug}
                            slug={c.slug}
                            active={category === c.slug}
                            count={counts[c.slug]}
                            onClick={() => updateParam("category", c.slug)}
                        />
                    ))}
                </div>
            </div>

            {/* Mobile toggle */}
            <div className="md:hidden mb-4 grid grid-cols-2 gap-2">
                <button
                    data-testid="mobile-view-list"
                    onClick={() => setView("list")}
                    className={`py-2 border font-oswald uppercase tracking-wider text-sm ${
                        view === "list" ? "bg-volt text-black border-volt" : "border-white/15 text-[#94a3b8]"
                    }`}
                >
                    <List className="w-4 h-4 inline mr-2" /> List
                </button>
                <button
                    data-testid="mobile-view-map"
                    onClick={() => setView("map")}
                    className={`py-2 border font-oswald uppercase tracking-wider text-sm ${
                        view === "map" ? "bg-volt text-black border-volt" : "border-white/15 text-[#94a3b8]"
                    }`}
                >
                    <MapIcon className="w-4 h-4 inline mr-2" /> Map
                </button>
            </div>

            {/* Split view */}
            <div className="grid md:grid-cols-12 gap-4">
                <div
                    className={`md:col-span-7 lg:col-span-7 ${
                        view === "map" ? "hidden md:block" : ""
                    }`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="mono-label">
                            {loading ? "SCANNING..." : `${data.length} TARGET${data.length === 1 ? "" : "S"} LOCATED`}
                        </div>
                    </div>

                    {!loading && data.length === 0 && (
                        <div className="tactical-card p-8 text-center">
                            <div className="font-oswald text-lg uppercase mb-2">No signals detected</div>
                            <p className="text-sm text-[#94a3b8]">
                                Widen your search radius or try a different sector.
                            </p>
                        </div>
                    )}

                    <div
                        data-testid="discount-list"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[calc(100vh-360px)] md:max-h-[calc(100vh-260px)] overflow-y-auto pr-1"
                    >
                        {data.map((d, i) => (
                            <div
                                key={d.id}
                                onMouseEnter={() => handleCardHover(d.id)}
                                onMouseLeave={() => setActiveId(null)}
                            >
                                <DiscountCard discount={d} index={i} />
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    className={`md:col-span-5 lg:col-span-5 sticky top-20 h-[calc(100vh-160px)] ${
                        view === "list" ? "hidden md:block" : ""
                    }`}
                >
                    <MapView
                        discounts={data}
                        activeId={activeId}
                        center={mapCenter}
                        onMarkerClick={setActiveId}
                    />
                </div>
            </div>
        </div>
    );
}
